import { DashboardApi20240730 } from '@sequencehq/api/dashboard/v20240730'
import {
  toPercentage,
  calculateDiscountedPrice,
  toBillingFrequencyLabel,
  toDiscountAmount,
  toMoney,
  toPriceSummary,
  parseBillingSchedulePhaseDuration
} from '@sequencehq/utils'
import {
  Duration,
  format,
  formatDuration,
  isAfter,
  isBefore
} from '@sequencehq/utils/dates'
import { useScheduleDates } from 'BillingScheduleHome/utils/useScheduleDates'
import {
  BillingSchedulePhaseWithParsedDates,
  useBillingScheduleAggregate
} from 'modules/BillingScheduleHome/utils/useBillingScheduleAggregate'
import { useMemo } from 'react'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'

type APIPrice = Exclude<DashboardApi20240730.GetPrice.Response['data'], null>
type APIBillingSchedulePhase =
  DashboardApi20240730.GetBillingSchedule.BillingSchedulePhase
type APIDiscount = APIBillingSchedulePhase['discounts'][0]

type ResolvedPhase = Omit<APIBillingSchedulePhase, 'startDate' | 'endDate'> & {
  prices: Array<APIPrice>
  duration: 'MILESTONE' | 'OPEN_ENDED' | Duration
  isAfterMilestonePhase: boolean
  startDate: Date
  endDate?: Date
}

export type TableProductInformation = {
  id: string
  priceId: string
  name: string
  summary: {
    billingFrequency: string
    billingType: string
    total: string
    discount?: string
    totalAfterDiscount?: string
  }
}

export type TablePhase = {
  id: string
  name?: string
  phaseHasDiscount: boolean
  isPhaseBillingReset: boolean
  recurrenceDayOfMonth?: number
  dateSummary: {
    isAfterMilestonePhase: boolean
    startDate: string
    endDate?: string
    duration?: string
  }
  productsWithNoMinimum: Array<TableProductInformation>
  minimum?: {
    products: Array<TableProductInformation>
    summary: {
      billingFrequency: string
      minimumAmount: string
    }
  }
  globalDiscount?: {
    amount: string
  }
  startDate: Date
  endDate?: Date
}

const orderPhasesByStartDate = (
  phases: (Omit<
    DashboardApi20240730.GetBillingSchedule.BillingSchedulePhase,
    'startDate' | 'endDate'
  > & {
    startDate: Date
    endDate: Date | undefined
  })[]
) => {
  return phases.sort(({ startDate: startDateA }, { startDate: startDateB }) => {
    if (!startDateA) {
      return 1
    }
    if (!startDateB) {
      return -1
    }
    return isAfter(startDateA, startDateB) ? 1 : -1
  })
}

const formatProductPrice = ({
  price,
  ownDiscount
}: {
  price: APIPrice
  ownDiscount?: APIDiscount
}): TableProductInformation => {
  /**
   * Only the price's own discount comes off the rate shown here. A phase-wide
   * discount covers the whole phase, so it gets its own row instead.
   */
  const totalAfterDiscount = calculateDiscountedPrice({
    price,
    discount: ownDiscount && {
      ...ownDiscount,
      discountCalculationType: ownDiscount.type
    }
  })

  return {
    id: price.productId,
    priceId: price.id,
    name: price.name,
    summary: {
      total: toPriceSummary(price),
      totalAfterDiscount:
        typeof totalAfterDiscount === 'number'
          ? toMoney({
              value: totalAfterDiscount.toString(),
              currency: price.currency
            })
          : undefined,
      billingType:
        price.billingType === 'IN_ADVANCE' ? 'In advance' : 'In arrears',
      billingFrequency: toBillingFrequencyLabel(price.billingFrequency),
      discount: ownDiscount
        ? toDiscountAmount({
            discountCalculationType: ownDiscount.type,
            discountAmount: ownDiscount.amount,
            currency: price.currency
          })
        : undefined
    }
  }
}

const formatEndDate = (resolvedPhase: ResolvedPhase): string | undefined => {
  if (resolvedPhase.duration === 'OPEN_ENDED') {
    return 'Open ended'
  }

  if (resolvedPhase.duration === 'MILESTONE') {
    return 'Milestone'
  }

  if (resolvedPhase.endDate) {
    return format(resolvedPhase.endDate, 'd LLLL yyyy')
  }

  return undefined
}

const parsePhaseInformation = (resolvedPhase: ResolvedPhase): TablePhase => {
  const phaseMinimum = resolvedPhase.minimums[0]
  const phaseCurrency = resolvedPhase.prices[0]?.currency

  invariant(phaseCurrency, 'Schedule must have a currency')

  const pricesWithMinimum = phaseMinimum
    ? resolvedPhase.prices.filter(price => {
        if (!phaseMinimum.restrictToPrices.length) {
          return true
        }

        return phaseMinimum.restrictToPrices.includes(price.id)
      })
    : []
  const pricesWithNoMinimum = resolvedPhase.prices.filter(price => {
    return !pricesWithMinimum.find(p => p.id === price.id)
  })
  const globalDiscount = resolvedPhase.discounts.find(
    d => !d.restrictToPrices.length
  )

  return {
    id: resolvedPhase.id,
    name: resolvedPhase.name,
    startDate: resolvedPhase.startDate,
    endDate: resolvedPhase.endDate,
    phaseHasDiscount: resolvedPhase.discounts.length > 0,
    isPhaseBillingReset: resolvedPhase.recurrencePreference === 'RESET',
    dateSummary: {
      isAfterMilestonePhase: resolvedPhase.isAfterMilestonePhase,
      startDate: format(resolvedPhase.startDate, 'd LLLL yyyy'),
      endDate: formatEndDate(resolvedPhase),
      duration: match(resolvedPhase)
        .with({ duration: 'OPEN_ENDED' }, () => {
          return 'Open ended'
        })
        .with({ duration: 'MILESTONE' }, () => {
          return 'Milestone'
        })
        .otherwise(() => {
          if (typeof resolvedPhase.duration === 'string') {
            return undefined
          }

          return formatDuration(
            {
              ...resolvedPhase.duration,
              days:
                resolvedPhase.duration.days === -1
                  ? 0
                  : resolvedPhase.duration.days
            },
            {
              format: ['years', 'months', 'days']
            }
          )
        })
    },
    globalDiscount: globalDiscount && {
      amount: `${
        globalDiscount.type === 'NOMINAL'
          ? toMoney({
              value: globalDiscount.amount.toString(),
              currency: phaseCurrency
            })
          : toPercentage(globalDiscount.amount.toString())
      } off`
    },
    minimum: phaseMinimum && {
      summary: {
        minimumAmount: toMoney({
          value: phaseMinimum.amount.toString(),
          currency: phaseCurrency
        }),
        billingFrequency: toBillingFrequencyLabel(
          pricesWithMinimum[0].billingFrequency
        )
      },
      products: pricesWithMinimum.map(price => {
        const ownDiscount = resolvedPhase.discounts.find(discount =>
          discount.restrictToPrices.includes(price.id)
        )

        return formatProductPrice({
          price,
          ownDiscount
        })
      })
    },
    productsWithNoMinimum: pricesWithNoMinimum.map(price => {
      const ownDiscount = resolvedPhase.discounts.find(discount =>
        discount.restrictToPrices.includes(price.id)
      )

      return formatProductPrice({
        price,
        ownDiscount
      })
    })
  }
}

export const orderAndFormatPhases = (data: {
  phases: Array<BillingSchedulePhaseWithParsedDates>
  prices: Array<APIPrice>
}): Array<TablePhase> => {
  const orderedPhases = orderPhasesByStartDate(data.phases)
  const phasesWithParsedDateInformation = orderedPhases.map(phase => {
    const startDate = phase.startDate
    const endDate = phase.endDate

    const duration = parseBillingSchedulePhaseDuration({
      startDate,
      endDate
    })

    return {
      ...phase,
      startDate,
      endDate,
      duration
    }
  })

  const milestonePhaseIndex = phasesWithParsedDateInformation.findIndex(
    phase => phase.duration === 'MILESTONE'
  )

  const resolvedPhases: Array<ResolvedPhase> =
    phasesWithParsedDateInformation.map((phase, phaseIdx) => {
      return {
        ...phase,
        prices: phase.priceIds
          .map(priceId => data.prices.find(price => price.id === priceId))
          .filter((price): price is APIPrice => Boolean(price)),
        isAfterMilestonePhase:
          milestonePhaseIndex !== -1 && phaseIdx > milestonePhaseIndex
      }
    })

  return resolvedPhases.map(parsePhaseInformation)
}

export const useSchedulePricingSection = ({
  billingScheduleId
}: {
  billingScheduleId: string
}) => {
  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId,
    select: data => {
      return {
        scheduleStartDate: data.billingSchedule.startDate,
        scheduleEndDate: data.billingSchedule.endDate,
        phases: orderAndFormatPhases({
          phases: data.billingSchedule.phases,
          prices: data.prices
        })
      }
    }
  })
  const { phaseRecurrenceDates } = useScheduleDates({
    billingScheduleId: billingScheduleId
  })

  /**
   * Expand either the current phase (i.e. the start date is before
   * today and the end date is after), the first phase if the
   * schedule hasn't started yet, or the last phase if the
   * schedule has ended.
   */
  const initialPhaseToExpand = useMemo(() => {
    if (!billingScheduleAggregateQuery.data) {
      return
    }

    const today = new Date()
    if (isBefore(today, billingScheduleAggregateQuery.data.scheduleStartDate)) {
      return billingScheduleAggregateQuery.data.phases[0]?.id
    }

    if (billingScheduleAggregateQuery.data.scheduleEndDate) {
      if (isAfter(today, billingScheduleAggregateQuery.data.scheduleEndDate)) {
        return billingScheduleAggregateQuery.data.phases.at(-1)?.id
      }
    }

    return billingScheduleAggregateQuery.data.phases.find(phase => {
      return (
        isBefore(phase.startDate, today) &&
        (!phase.endDate || isAfter(phase.endDate, today))
      )
    })?.id
  }, [billingScheduleAggregateQuery.data])

  return {
    isLoading: billingScheduleAggregateQuery.isPending,
    isError: billingScheduleAggregateQuery.isError,
    phases: billingScheduleAggregateQuery.data?.phases.map((phase, idx) => ({
      ...phase,
      recurrenceDayOfMonth: phaseRecurrenceDates?.[idx]?.recurrenceDayOfMonth
    })),
    initialPhaseToExpand
  }
}
